import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import SiteHeader, { CalendlyLink, SmartLink } from "./SiteHeader";
import { ReviewSchema } from "./Testimonials";
import { FOOTER_LIVE as FOOTER_COLUMNS, LOGO, PHONE, PHONE_TEL, EMAIL, ALBERTA_CITIES, ALBERTA_SERVICES, SOCIAL_LINKS } from "./nav";
import { Instagram, Facebook } from "lucide-react";

const SpotifyIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 1.5C6.2 1.5 1.5 6.2 1.5 12S6.2 22.5 12 22.5 22.5 17.8 22.5 12 17.8 1.5 12 1.5zm4.8 15.2a.65.65 0 0 1-.9.22c-2.46-1.5-5.56-1.84-9.2-1.01a.65.65 0 1 1-.29-1.27c3.99-.91 7.41-.52 10.17 1.16.31.19.41.59.22.9zm1.28-2.86a.82.82 0 0 1-1.12.27c-2.82-1.73-7.11-2.23-10.44-1.22a.82.82 0 1 1-.47-1.56c3.8-1.15 8.53-.59 11.77 1.4.38.23.5.73.26 1.11zm.11-2.98C14.8 8.87 9.24 8.68 6.02 9.66a.98.98 0 1 1-.57-1.87c3.7-1.12 9.85-.9 13.73 1.4a.98.98 0 0 1-1 1.68z" /></svg>
);
import { VipClubForm } from "./VipClubForm";
import HelloProof from "./HelloProof";
import { isFullDirectory, compactDirectory } from "./directory";
import COPY from "./content/siteCopy.json";
import "./site.css";

// Loads Elfsight platform + Calendly once per app shell; re-inits Elfsight on route change.
function useThirdParty() {
  useEffect(() => {
    const add = (src, id) => {
      if (document.getElementById(id)) return;
      const s = document.createElement("script"); s.src = src; s.async = true; s.id = id; document.body.appendChild(s);
    };
    if (!document.getElementById("calendly-css")) {
      const l = document.createElement("link"); l.rel = "stylesheet"; l.id = "calendly-css";
      l.href = "https://assets.calendly.com/assets/external/widget.css"; document.head.appendChild(l);
    }
    add("https://assets.calendly.com/assets/external/widget.js", "calendly-js");
  }, []);
}

// Context-aware local directory (brief §Global footer). Default = Alberta homepage variant (source copy).
export function LocalDirectory({ directory }) {
  const d = directory || {
    word: '"hello, Alberta."', eyebrow: "Explore Hello Mortgage", heading: COPY.explore.heading, text: COPY.explore.text,
    kicker: "AB", kickerLabel: COPY.explore.places_label, places: ALBERTA_CITIES.map(([n, h]) => [`${n} mortgage broker`, h]),
    serviceLabel: COPY.explore.services_label, services: ALBERTA_SERVICES,
  };
  // Services card first and larger (the page's own offer); nearby-community links keep their crawlable,
  // descriptive anchors in the smaller second card. Both titles are H3s under the section H2.
  return (
    <section className="explore" aria-labelledby="explore-heading" style={{ "--explore-word": d.word }} data-testid="local-directory">
      <div className="wrap">
        <div className="explore-head">
          <div><div className="eyebrow">{d.eyebrow}</div><h2 className="display explore-title" id="explore-heading">{d.heading}</h2></div>
          <p>{d.text}</p>
        </div>
        <div className="directory-grid">
          <div className="directory-panel directory-panel--services" data-testid="directory-services">
            <h3 className="directory-kicker"><span aria-hidden="true">+</span>{d.serviceLabel}</h3>
            <div className="service-links">{d.services.map(([l, h]) => <SmartLink key={h} className="service-link" href={h}>{l}</SmartLink>)}</div>
          </div>
          <div className="directory-panel directory-panel--places" data-testid="directory-places">
            <h3 className="directory-kicker"><span aria-hidden="true">{d.kicker}</span>{d.kickerLabel}</h3>
            <div className="city-links">{d.places.map(([l, h]) => <SmartLink key={h} className="city-link" href={h}>{l}</SmartLink>)}</div>
            {d.areas && (
              <div className="area-strip" data-testid="areas-served">
                <div className="directory-kicker"><span aria-hidden="true">•</span>{d.areas.label}</div>
                <p className="area-text">{d.areas.text}</p>
                <ul className="area-chips">{d.areas.items.map((n) => <li key={n} className="area-chip">{n}</li>)}</ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function SiteFooter({ minimal }) {
  return (
    <footer data-testid="site-footer">
      {!minimal && <div className="wrap footer-vip"><VipClubForm /></div>}
      <div className="wrap footer-main">
        <div className="footer-brand">
          <img className="footer-logo" src="/brand/hello-mortgage-team-logo-footer.png" alt="Hello Mortgage Team" />
          <p className="footer-tagline">Alberta-based, Hello Mortgage Team blends award-winning education, powerful technology, and legendary service to deliver a radically different mortgage experience for clients and referral partners across Alberta — one that truly makes it the easiest way to mortgage.</p>
          <div className="footer-brand-links">
            <a href={PHONE_TEL} data-testid="footer-phone">{PHONE}</a>
            <a href={`mailto:${EMAIL}`} data-testid="footer-email">{EMAIL}</a>
            <div className="footer-social" data-testid="footer-social">
              {SOCIAL_LINKS.map(([name, href]) => {
                const Icon = name === "Instagram" ? Instagram : name === "Facebook" ? Facebook : SpotifyIcon;
                return <a key={name} href={href} target="_blank" rel="noopener" aria-label={name === "Spotify" ? "Hello Mortgage mixtapes on Spotify" : `Hello Mortgage on ${name}`} title={name === "Spotify" ? "Our mixtapes on Spotify" : name} data-testid={`footer-social-${name.toLowerCase()}`}><Icon size={18} strokeWidth={2} /></a>;
              })}
            </div>
            <CalendlyLink className="footer-book" testId="footer-book">Book a conversation</CalendlyLink>
          </div>
        </div>
        {FOOTER_COLUMNS.map((c) => (
          <nav className="footer-column" aria-label={c.aria} key={c.title}>
            <div className="footer-title">{c.title}</div>
            {c.links.map(([l, h]) => <SmartLink key={l} href={h}>{l}</SmartLink>)}
            {c.sub && <><div className="footer-title footer-subtitle" data-testid="footer-partners-title">{c.sub.title}</div>{c.sub.links.map(([l, h]) => <SmartLink key={l} href={h}>{l}</SmartLink>)}</>}
          </nav>
        ))}
      </div>
      <div className="footer-bottom">
        <div className="wrap footer-bottom-row">
          <span>© {new Date().getFullYear()} Hello Mortgage Team · Powered by BRX Mortgage · <em className="footer-slogan">Say Hello To The Easiest Way To Mortgage</em></span>
          <div className="footer-bottom-links"><SmartLink href="/privacy/">Privacy</SmartLink></div>
        </div>
      </div>
    </footer>
  );
}


function useTrack(pathname) {
  useEffect(() => {
    if (/bot|crawl|spider|headless/i.test(navigator.userAgent)) return;
    let sid = null;
    try { sid = sessionStorage.getItem("hm_sid"); if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem("hm_sid", sid); } } catch { return; }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/track`, { method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true,
      body: JSON.stringify({ path: pathname, sid, ref: document.referrer || null, w: window.innerWidth }) }).catch(() => {});
  }, [pathname]);
}

export default function SiteLayout({ children, directory, bodyClass = "", outside = null, minimal = false, loading = false }) {
  useThirdParty();
  const { pathname, hash } = useLocation();
  useTrack(pathname);
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    // Hash targets render after async data lands and the page keeps growing — poll for the element, then re-align once layout settles.
    const id = decodeURIComponent(hash.slice(1));
    const go = () => { const el = document.getElementById(id); if (el) el.scrollIntoView({ block: "start" }); return !!el; };
    let tries = 0;
    const t = setInterval(() => { if (go() || ++tries > 40) { clearInterval(t); setTimeout(go, 700); setTimeout(go, 1800); } }, 100);
    return () => clearInterval(t);
  }, [pathname, hash]);
  if (outside) {
    return (
      <>
        <div className="hm-site" data-testid="site-shell" style={{ position: "sticky", top: 0, zIndex: 20 }}>
          <Helmet><link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap" rel="stylesheet" /></Helmet>
          <SiteHeader />
        </div>
        {outside}
        <div className="hm-site"><SiteFooter /><HelloProof /></div>
      </>
    );
  }
  return (
    <div className={`hm-site ${bodyClass}`} data-testid="site-shell">
      <Helmet>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap" rel="stylesheet" />
      </Helmet>
      <ReviewSchema />
      <SiteHeader />
      <main id="top" className={loading ? "is-loading" : undefined}>{children}{!minimal && !loading && <LocalDirectory directory={directory || (isFullDirectory(pathname) ? undefined : compactDirectory(pathname))} />}</main>
      {!loading && <SiteFooter minimal={minimal} />}
      <HelloProof />
    </div>
  );
}
